var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Idp } from "../idp";
import { CobrowserKey } from "./model";
import { Messenger } from "./messenger";
import { Observer } from "../events/observer";
import { log } from "../../utils";
import { Animate } from "../events/animate";
export const SIGNATURE_KEY = `${CobrowserKey}_signature`;
export const ID_KEY = `${CobrowserKey}_id`;
export class PageBase {
    constructor(target, user = null) {
        var _a;
        this.target = target;
        this.user = user;
        this.documentId = 0;
        this.allowedOrigins = "";
        this.iframeOriginsFilter = "";
        /** allows parent to match current window to frameElement as same origin messages share window source */
        this.signature = this.generateId();
        this.isMobile = (() => {
            try {
                return navigator.maxTouchPoints > 1;
            }
            catch (_a) {
                return false;
            }
        })();
        // distance from viewport origin
        this.absoluteLeft = 0;
        this.absoluteTop = 0;
        this.idp = new Idp();
        this.ecounter = 0;
        this.trackedIframes = [];
        this.iframeOnloadTimeout = {};
        /** common time for all events produced during flushing */
        this.flushingTime = 0;
        this.lastSync = Date.now();
        this.cleared = false;
        if (this.target.tagName === "IFRAME") {
            this.defaultView = (_a = this.target.contentDocument) === null || _a === void 0 ? void 0 : _a.defaultView;
        }
        else {
            this.defaultView = this.target.ownerDocument.defaultView;
        }
        this.observer = new Observer(this);
        this.animate = new Animate(this);
        this.messenger = new Messenger(this);
        this.idp.set(this.getDocument(), 1);
    }
    get href() {
        var _a;
        return this.getDocument()
            ? (_a = this.getWindow()) === null || _a === void 0 ? void 0 : _a.location.href
            : this.target.getAttribute("src");
    }
    onSetEvents(events) {
        this.setEvents(events);
    }
    /** @deprecated */
    get enabledEvents() {
        return {
            cursor: this.cursor.enabled,
            input: this.input.enabled,
            scroll: this.scroll.enabled,
            highlight: this.highlighter.enabled,
        };
    }
    setEvents(events) {
        if (events.input) {
            this.input.track(this.getDocument());
        }
        else if (events.input === false) {
            this.input.untrack();
        }
        if (events.cursor) {
            this.cursor.track(this.getDocument());
        }
        else if (events.cursor === false) {
            this.cursor.untrack();
        }
        if (events.scroll) {
            this.scroll.track();
        }
        else if (events.scroll === false) {
            this.scroll.untrack();
        }
        if (events.highlight) {
            this.highlighter.track();
        }
        else if (events.highlight === false) {
            this.highlighter.untrack();
        }
        const iframes = this.trackedIframes;
        for (let i = 0; i < iframes.length; i++) {
            if (!iframes[i].isConnected) {
                this.trackedIframes = this.trackedIframes.filter((iframe) => iframe !== iframes[i]);
            }
            if (this.messenger.maybeUsesOldMode(iframes[i].contentWindow)) {
                this.messenger.sendSetEvents(events, iframes[i].contentWindow);
            }
        }
    }
    getWindow() {
        return this.defaultView.window;
    }
    getDocument() {
        return this.defaultView.document;
    }
    handleRemoteInput(event) {
        this.input.dispatch(event.payload);
    }
    handleRemoteScroll(ev) {
        this.scroll.dispatch(ev.payload, ev.time);
    }
    handleRemoteCursor(ev) {
        this.cursor.dispatch(ev.payload);
    }
    resetAbsolutePosition(coords) {
        this.absoluteLeft = coords.left;
        this.absoluteTop = coords.top;
        const iframes = this.trackedIframes;
        for (let i = 0; i < iframes.length; i++) {
            if (!iframes[i].isConnected) {
                this.trackedIframes = this.trackedIframes.filter((iframe) => iframe !== iframes[i]);
            }
            else if (iframes[i].contentWindow) {
                const rect = iframes[i].getBoundingClientRect();
                this.messenger.sendAbsolutePositionReset({
                    left: this.absoluteLeft + rect.left,
                    top: this.absoluteTop + rect.top,
                }, iframes[i].contentWindow);
            }
        }
    }
    onStatusRequest(ev, source) {
        const iframe = this.lookupFrame(source, ev.signature);
        const targetId = this.idp.getId(iframe);
        if (targetId) {
            this.messenger.sendPleaseRegister(this.generateId(), iframe.contentWindow);
        }
    }
    onIsRegisteredResponse(ev) {
        if (ev.id in this.iframeOnloadTimeout) {
            window.clearTimeout(this.iframeOnloadTimeout[ev.id].timeoutId);
            delete this.iframeOnloadTimeout[ev.id];
        }
    }
    onRegistrationRequest(ev, source) {
        if (ev.id in this.iframeOnloadTimeout) {
            this.messenger.sendPageUpdate(this.iframeOnloadTimeout[ev.id].event);
            delete this.iframeOnloadTimeout[ev.id];
        }
        const iframe = this.lookupFrame(source, ev.signature);
        const targetId = this.idp.getId(iframe);
        log("onRegistrationRequest iframe has targetId", targetId);
        if (targetId) {
            this.messenger.sendDocumentRegistered(ev.id, {
                targetId,
                parentId: this.documentId,
                allowOrigins: this.options.allowOrigins,
                filters: this.filter.filters,
                user: this.options.user,
            }, iframe.contentWindow);
        }
    }
    onFlushRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.flushingTime)
                return;
            try {
                this.flushingTime = this.getTime();
                const records = yield this.observer.flush();
                if (records) {
                    records.metrics = { captureTime: Date.now() - this.flushingTime };
                    this.messenger.sendPageUpdate(this.event("Mutations", records));
                }
                const anims = this.animate.flush();
                if (anims) {
                    this.messenger.sendPageUpdate(this.event("Mutations", anims));
                }
            }
            finally {
                this.messenger.sendFlushResponse(this.observer.postProcess().events);
                this.flushingTime = 0;
            }
        });
    }
    /** @deprecated */
    onDocumentReady(source) {
        const target = this.lookupFrame(source);
        if (target) {
            this.messenger.sendInitCapture({
                documentId: this.generateId(),
                targetId: this.idp.getId(target),
                parentId: this.documentId,
                endpoint: this.user.endpoint,
                filters: this.filter.filters,
            }, target.contentWindow);
        }
    }
    lookupFrame(source, signature) {
        const iframes = this.trackedIframes;
        for (let i = 0; i < iframes.length; i++) {
            if (!iframes[i].isConnected) {
                this.trackedIframes = this.trackedIframes.filter((iframe) => iframe !== iframes[i]);
            }
            else if ((iframes[i].contentDocument &&
                iframes[i].contentWindow[SIGNATURE_KEY] === signature) ||
                iframes[i].contentWindow === source) {
                return iframes[i];
            }
        }
        return null;
    }
    generateId() {
        return Math.trunc(((Date.now() % Math.pow(10, 8)) + Math.random()) * 10000);
    }
    /**
     * Cover cases:
     * 1. Iframe relocated
     *   - onload called: no attachment/ no id / with id; or trackFrame
     * 2. Iframe reloaded
     * 3. Iframe removed
     * 4. Iframe tracked
     *
     * @param iframe
     * @returns void
     */
    trackFrame(target_1) {
        return __awaiter(this, arguments, void 0, function* (target, init = true) {
            var _a, _b, _c;
            log("trackframe, origin; connected; cleared;", target.contentDocument
                ? (_a = target.contentWindow) === null || _a === void 0 ? void 0 : _a.location.href
                : target.getAttribute("src"), target.isConnected, this.cleared);
            // has been removed while waiting to load
            if (!target.isConnected || this.cleared) {
                return;
            }
            const id = this.generateId();
            const src = target.contentDocument
                ? (_b = target.contentWindow) === null || _b === void 0 ? void 0 : _b.location.href
                : target.getAttribute("src");
            const isPdf = ((_c = target.contentDocument) === null || _c === void 0 ? void 0 : _c.contentType) === "application/pdf" ||
                (src === null || src === void 0 ? void 0 : src.includes(".pdf"));
            if (!init) {
                const mode = isPdf
                    ? "pdf"
                    : target.contentDocument
                        ? "same-origin"
                        : "cross-origin";
                const event = this.event("Mutations", {
                    iframe: {
                        [this.idp.getId(target)]: {
                            mode,
                            src,
                        },
                    },
                });
                // onload might have been faux, wait to see if we get any message back
                if (mode === "cross-origin") {
                    this.iframeOnloadTimeout[id] = {
                        event,
                        timeoutId: window.setTimeout(() => {
                            delete this.iframeOnloadTimeout[id];
                            this.messenger.sendPageUpdate(event);
                        }, 700),
                    };
                }
                else
                    this.messenger.sendPageUpdate(event);
            }
            let sameOriginChild;
            target.addEventListener("load", () => {
                var _a;
                log("trackFrame onload; href; sameorigin", target.contentDocument
                    ? (_a = target.contentWindow) === null || _a === void 0 ? void 0 : _a.location.href
                    : target.getAttribute("src"), !!sameOriginChild);
                // remove duplicate
                sameOriginChild === null || sameOriginChild === void 0 ? void 0 : sameOriginChild.cleanup();
                // add some delay for proper cleanup and init
                setTimeout(() => this.trackFrame(target, false), 0);
            }, { once: true });
            if (isPdf) {
                return;
            }
            if (!this.trackedIframes.includes(target))
                this.trackedIframes.push(target);
            if (target.contentDocument) {
                target.contentWindow[ID_KEY] = { id };
                sameOriginChild = this.createIframe(target, {
                    allowOrigins: this.options.allowOrigins,
                });
            }
            else {
                // cross origin iframe might use the old script
                this.messenger.sendInitCapture({
                    documentId: id,
                    targetId: this.idp.getId(target),
                    parentId: this.documentId,
                    endpoint: this.user.endpoint,
                    filters: this.filter.filters,
                }, target.contentWindow);
            }
            this.messenger.sendPleaseRegister(id, target.contentWindow);
        });
    }
    get closed() {
        var _a;
        return this.cleared || ((_a = this.getWindow()) === null || _a === void 0 ? void 0 : _a.closed) !== false;
    }
    cleanup(shallow = false) {
        var _a;
        this.cleared = true;
        (_a = this.location) === null || _a === void 0 ? void 0 : _a.untrack();
        this.scroll.untrack();
        this.input.untrack();
        this.cursor.untrack();
        this.animate.untrack();
        this.cleanIdp();
        this.messenger.cleanup();
        this.trackedIframes = [];
    }
    /** clean everything related to an id */
    cleanIdp() {
        // this.queue = this.queue[0]?.type === "PageMetadata" ? [this.queue[0]] : [];
        this.idp.reset();
        this.idp.set(this.getDocument(), 1);
    }
    getTime() {
        return Date.now();
    }
    event(type, payload, document = this.documentId) {
        return {
            type,
            time: this.flushingTime || this.getTime(),
            document,
            endpoint: this.user.endpoint,
            payload,
        };
    }
}
PageBase.SyncRequestPeriod = 5000;
//# sourceMappingURL=base.js.map